import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { IEmpresa } from '../types/empresa.types';
import { empresaService } from '../services/empresa.service';
import { useAuth } from './AuthContext';

interface EmpresaContextType {
  empresas: IEmpresa[];
  empresaActual: IEmpresa | null;
  setEmpresaActual: (empresa: IEmpresa | null) => void;
  loading: boolean;
  refetch: () => void;
}

const EmpresaContext = createContext<EmpresaContextType | undefined>(undefined);

export const EmpresaProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [empresas, setEmpresas] = useState<IEmpresa[]>([]);
  const [empresaActual, setEmpresaActual] = useState<IEmpresa | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchEmpresas = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await empresaService.getAll();
      const listaEmpresas = response.data || [];
      setEmpresas(listaEmpresas);

      if (listaEmpresas.length > 0) {
        if (user?.empresaId) {
          const emp = listaEmpresas.find((e) => e.id === user.empresaId);
          setEmpresaActual(emp || listaEmpresas[0]);
        } else {
          setEmpresaActual(listaEmpresas[0]);
        }
      } else {
        setEmpresaActual(null);
      }
    } catch (error) {
      console.error('Error cargando empresas:', error);
      setEmpresas([]);
      setEmpresaActual(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.empresaId]);

  useEffect(() => {
    fetchEmpresas();
  }, [user?.empresaId, isAuthenticated, fetchEmpresas]);

  return (
    <EmpresaContext.Provider
      value={{
        empresas,
        empresaActual,
        setEmpresaActual,
        loading,
        refetch: fetchEmpresas,
      }}
    >
      {children}
    </EmpresaContext.Provider>
  );
};

export const useEmpresa = () => {
  const context = useContext(EmpresaContext);
  if (context === undefined) {
    throw new Error('useEmpresa debe usarse dentro de EmpresaProvider');
  }
  return context;
};
